import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { ConfigRow } from '../display/ConfigRow'
import { ConfigSection } from '../display/ConfigSection'
import type { ConfigQueryResult } from '../PartnerConfig.types'

import { Flex, Typography } from '@percent/lemonade'
import type {
  GivingDatePeriodConfigurationValue,
  VolunteeringAllowanceConfigValue
} from '@percent/admin-dashboard/api/types'

type ResetDatesSectionProps = {
  givingDatePeriod: ConfigQueryResult<GivingDatePeriodConfigurationValue>
  volunteeringAllowance: ConfigQueryResult<VolunteeringAllowanceConfigValue>
  achievementResetDate: ConfigQueryResult<string>
  hasVolunteeringScope: boolean
}

export function ResetDatesSection({
  givingDatePeriod,
  volunteeringAllowance,
  achievementResetDate,
  hasVolunteeringScope
}: ResetDatesSectionProps) {
  const { t } = useTranslation()

  return (
    <ConfigSection title={t('partnerConfig.section.resetDates')}>
      <ConfigRow
        id="resetDates.giving"
        label={t('partnerConfig.resetDates.giving')}
        tooltip={t('partnerConfig.tooltip.resetDatesGiving')}
        isLoading={givingDatePeriod.isLoading}
        error={givingDatePeriod.error}
      >
        {givingDatePeriod.data ? (
          <Flex direction="column" gap={1} align="flex-end">
            <Typography variant="default">{givingDatePeriod.data.date}</Typography>
            <Typography variant="small" $color="gray500">
              {t('partnerConfig.resetDates.period.year')}
            </Typography>
          </Flex>
        ) : undefined}
      </ConfigRow>

      {hasVolunteeringScope && (
        <ConfigRow
          id="resetDates.volunteeringAllowance"
          label={t('partnerConfig.resetDates.volunteeringAllowance')}
          tooltip={t('partnerConfig.tooltip.resetDatesVolunteeringAllowance')}
          isLoading={volunteeringAllowance.isLoading}
          error={volunteeringAllowance.error}
        >
          {volunteeringAllowance.data ? (
            <Flex direction="column" gap={1} align="flex-end">
              <Typography variant="default">{volunteeringAllowance.data.renewsAt}</Typography>
              <Typography variant="small" $color="gray500">
                {`${volunteeringAllowance.data.allowanceMinutes} ${t('partnerConfig.resetDates.minutesUnit')} · ${t(
                  `partnerConfig.periodicity.${volunteeringAllowance.data.periodicity}`
                )}`}
              </Typography>
            </Flex>
          ) : undefined}
        </ConfigRow>
      )}

      <ConfigRow
        id="resetDates.achievementResetDate"
        label={t('partnerConfig.resetDates.achievementResetDate')}
        tooltip={t('partnerConfig.tooltip.resetDatesAchievementResetDate')}
        isLoading={achievementResetDate.isLoading}
        error={achievementResetDate.error}
      >
        {achievementResetDate.data ? (
          <Typography variant="default">{format(new Date(achievementResetDate.data), 'dd MMM yyyy, HH:mm')}</Typography>
        ) : undefined}
      </ConfigRow>
    </ConfigSection>
  )
}
